// LomavisRowSelect1.tsx
import React from 'react';
import { Stack, Form } from 'react-bootstrap';
import { translateComponent } from '@/utils/tolgee/translateComponent';
import { LomavisOverlayToolTip1 } from '../../overlays/lomavisOverlayToolTip1/lomavisOverlayToolTip1';

interface LomavisRowSelect1Props {
  checked: boolean;
  onChange?: (checked: boolean) => void;
  imageSrc: string;
  title: string;
  subtitle?: string;
  /** "active" | "paused" | "cancel" (case-insensitive) */
  status?: string;
  showStatus?: boolean;
  extra?: React.ReactNode;
}

/**
 * Atoms: LomavisRowSelect1
 */
export const LomavisRowSelect1: React.FC<LomavisRowSelect1Props> = ({
  checked,
  onChange,
  imageSrc,
  title,
  subtitle,
  status,
  showStatus = true,
  extra
}) => {
  const t = translateComponent('LomavisRowSelect1');
  const hasSecondaryLine = Boolean(subtitle || (showStatus && status));

  // map status → color
  const normalized = status?.trim().toLowerCase();
  const statusClass =
    normalized === 'active'
      ? 'text-success'
      : normalized === 'paused'
      ? 'text-warning'
      : 'text-danger';

  // image error/empty handling (no loading state)
  const [imgError, setImgError] = React.useState<boolean>(!imageSrc);
  React.useEffect(() => {
    setImgError(!imageSrc);
  }, [imageSrc]);

  // simple initials for placeholder
  const initials = React.useMemo(() => {
    const parts = (title || '').trim().split(/\s+/).filter(Boolean);
    const a = parts[0]?.[0] ?? '';
    const b = parts[1]?.[0] ?? '';
    const s = (a + b).toUpperCase();
    return s || '—';
  }, [title]);

  return (
    <Stack
      direction="horizontal"
      gap={2}
      className="d-flex p-2 align-items-center"
    >
      {/* Checkbox */}
      {onChange && (
        <Form.Check className="m-0">
          <Form.Check.Input
            type="checkbox"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            style={{ cursor: 'pointer' }}
            aria-label={t('selectRow', 'Select row')}
          />
        </Form.Check>
      )}

      {/* Image (50px square) with error/empty fallback */}
      <div
        style={{
          width: 50,
          height: 50,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        aria-label={t('thumbnail', 'Thumbnail')}
        className="rounded"
      >
        {!imgError && imageSrc ? (
          <img
            key={imageSrc} // reset element on src change
            src={imageSrc}
            alt={title}
            loading="lazy"
            onError={() => setImgError(true)}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain'
            }}
            className="rounded"
          />
        ) : (
          <div
            className="bg-light border rounded d-flex align-items-center justify-content-center text-muted"
            style={{
              width: '100%',
              height: '100%',
              fontSize: 12,
              fontWeight: 600,
              userSelect: 'none'
            }}
            title={t('imageUnavailable', 'Image unavailable')}
          >
            {initials}
          </div>
        )}
      </div>

      {/* Text Content (truncate with ellipsis) */}
      <LomavisOverlayToolTip1
        text={subtitle ? `${title} — ${subtitle}` : title}
        placement="top"
        wrapperClassName="flex-grow-1"
        wrapperStyle={{ minWidth: 0 }}
      >
        <div className="d-flex p-2 align-items-center">
          <div className="flex-grow-1 overflow-hidden" style={{ minWidth: 0 }}>
            <div
              className={`text-truncate text-gray-800 fw-bold text-hover-primary fs-6 ${
                hasSecondaryLine ? 'mb-1' : ''
              }`}
              style={{ whiteSpace: 'nowrap' }}
              title={title}
            >
              {title}
            </div>
            {subtitle && (
              <div
                className="text-truncate text-gray-600 fw-semibold d-block fs-7"
                title={subtitle}
              >
                {subtitle}
              </div>
            )}
            {showStatus && status && (
              <div
                className="text-truncate text-gray-500 fw-semibold d-block fs-7"
                title={`${t('Status')}: ${t(status)}`}
              >
                {t('Status')}:{' '}
                <span className={`${statusClass} fw-medium`}>{t(status)}</span>
              </div>
            )}
          </div>
        </div>
      </LomavisOverlayToolTip1>

      {/* Optional Right Content */}
      {extra && <div className="ms-2">{extra}</div>}
    </Stack>
  );
};
